/**
 * LevelFunnelCharts — подвкладка «Графики» (общая часть, все режимы)
 * ======================================================================
 *
 * Строится из того же ответа /level-funnel/table, что и таблица — отдельного
 * chart-эндпоинта в контракте нет. TD получает те же три графика сериями по
 * сложности плюс отдельную панель winrate hard (§1.4 плана).
 */

import { levelAxisDomain } from './slice';
import React, { useMemo } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ReferenceLine } from 'recharts';
import { Tooltip } from '../../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../../ui/responsive-chart';
import { fmtInt, fmtPct } from './format';
import { levelFunnel as levelFunnelSpecs } from '@ggstat/chart-specs';

// Ряды, цвета и заголовки — общие с MCP (shared/chart-specs/specs/levelFunnel.json).
const { difficulties, funnel: FUNNEL, winrate: WINRATE, winrateTd: WINRATE_TD, winrateHard: WINRATE_HARD, volume: VOLUME } =
  levelFunnelSpecs;
const DIFF_LABEL = Object.fromEntries(difficulties.map((d) => [d.key, d.label]));
const DIFF_KEYS = difficulties.map((d) => d.key);

function winrate(group) {
  const denom = (group?.wins || 0) + (group?.losses || 0);
  return denom > 0 ? (group.wins / denom) * 100 : null;
}

function uniqueWinrate(group) {
  return group?.uniqueStarters ? (group.uniqueWinners / group.uniqueStarters) * 100 : null;
}

/** Тултип funnel-кривой: показывает и абсолютное число, и долю от базы. */
function FunnelTooltip({ active, payload, label, base, showAbs }) {
  if (!active || !payload?.length) return null;
  const value = payload[0].value;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium mb-1">Уровень {label}</p>
      <p>Стартовало: {fmtInt(value)} · {fmtPct(value, base, showAbs)}</p>
    </div>
  );
}

/** Тултип winrate-графиков: значения через fmtPct(num, den, showAbs). */
function RateTooltip({ active, payload, label, showAbs }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm space-y-0.5">
      <p className="font-medium mb-1">Уровень {label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}: {fmtPct(entry.payload[`${entry.dataKey}__num`], entry.payload[`${entry.dataKey}__den`], showAbs)}
        </p>
      ))}
    </div>
  );
}

export function LevelFunnelCharts({ tableData, mode, showAbs }) {
  const base = tableData?.base || 0;
  const installsCount = tableData?.fromInstalls ? tableData?.installs?.count : null;
  const isTd = mode === 'td';
  const rows = useMemo(() => tableData?.rows || [], [tableData]);

  const funnelData = useMemo(
    () => rows.map((r) => ({ level: r.level, starters: r.uniqueStartersAny })),
    [rows]
  );

  const winrateData = useMemo(() => {
    if (isTd) {
      return rows.map((r) => {
        const point = { level: r.level };
        DIFF_KEYS.forEach((d) => {
          const g = r.groups?.[d] || {};
          point[d] = winrate(g);
          point[`${d}__num`] = g.wins;
          point[`${d}__den`] = (g.wins || 0) + (g.losses || 0);
        });
        return point;
      });
    }
    return rows.map((r) => {
      const g = r.groups?.all || {};
      return {
        level: r.level,
        winrate: winrate(g),
        winrate__num: g.wins,
        winrate__den: (g.wins || 0) + (g.losses || 0),
        uniqueWinrate: uniqueWinrate(g),
        uniqueWinrate__num: g.uniqueWinners,
        uniqueWinrate__den: g.uniqueStarters
      };
    });
  }, [rows, isTd]);

  const hardWinrateData = useMemo(() => {
    if (!isTd) return [];
    return rows.map((r) => {
      const g = r.groups?.hard || {};
      return {
        level: r.level,
        winrate: winrate(g),
        winrate__num: g.wins,
        winrate__den: (g.wins || 0) + (g.losses || 0)
      };
    });
  }, [rows, isTd]);

  const volumeByDifficulty = useMemo(() => {
    if (!isTd) return null;
    return DIFF_KEYS.map((d) => ({
      key: d,
      label: DIFF_LABEL[d],
      data: rows.map((r) => {
        const g = r.groups?.[d] || {};
        return { level: r.level, starts: g.starts, wins: g.wins, losses: g.losses };
      })
    }));
  }, [rows, isTd]);

  const volumeData = useMemo(() => {
    if (isTd) return null;
    return rows.map((r) => {
      const g = r.groups?.all || {};
      return { level: r.level, starts: g.starts, wins: g.wins, losses: g.losses };
    });
  }, [rows, isTd]);

  return (
    <div className="space-y-8">
      <section>
        <p className="text-sm font-medium mb-2">{FUNNEL.title}</p>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={funnelData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="level" type="number" domain={levelAxisDomain(tableData)} allowDecimals={false} className="text-xs" />
            <YAxis className="text-xs" tickFormatter={fmtInt} />
            <Tooltip content={<FunnelTooltip base={base} showAbs={showAbs} />} />
            {installsCount != null && (
              <ReferenceLine
                y={installsCount}
                stroke={FUNNEL.installsLineColor}
                strokeDasharray="4 4"
                label={{ value: `Инсталлы: ${fmtInt(installsCount)}`, position: 'insideTopRight', fontSize: 11 }}
              />
            )}
            <Line
              type="monotone"
              dataKey={FUNNEL.series[0].key}
              name={FUNNEL.series[0].label}
              stroke={FUNNEL.series[0].color}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </section>

      <section>
        <p className="text-sm font-medium mb-2">
          {isTd ? WINRATE_TD.title : WINRATE.title}
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={winrateData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="level" type="number" domain={levelAxisDomain(tableData)} allowDecimals={false} className="text-xs" />
            <YAxis domain={[0, 100]} className="text-xs" tickFormatter={(v) => `${v}%`} />
            <Tooltip content={<RateTooltip showAbs={showAbs} />} />
            <Legend />
            {isTd
              ? WINRATE_TD.series.map((sr) => (
                  <Line
                    key={sr.key}
                    type="monotone"
                    dataKey={sr.key}
                    name={sr.label}
                    stroke={sr.color}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))
              : WINRATE.series.map((sr) => (
                  <Line
                    key={sr.key}
                    type="monotone"
                    dataKey={sr.key}
                    name={sr.label}
                    stroke={sr.color}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
          </LineChart>
        </ResponsiveContainer>
      </section>

      {isTd && (
        <section>
          <p className="text-sm font-medium mb-2">{WINRATE_HARD.title}</p>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={hardWinrateData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="level" type="number" domain={levelAxisDomain(tableData)} allowDecimals={false} className="text-xs" />
              <YAxis domain={[0, 100]} className="text-xs" tickFormatter={(v) => `${v}%`} />
              <Tooltip content={<RateTooltip showAbs={showAbs} />} />
              <Line
                type="monotone"
                dataKey={WINRATE_HARD.series[0].key}
                name={WINRATE_HARD.series[0].label}
                stroke={WINRATE_HARD.series[0].color}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </section>
      )}

      <section>
        <p className="text-sm font-medium mb-2">{VOLUME.title}</p>
        {isTd ? (
          <div className="grid gap-4 md:grid-cols-3">
            {volumeByDifficulty.map((block) => (
              <div key={block.key}>
                <p className="text-xs text-muted-foreground mb-1">{block.label}</p>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={block.data} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="level" type="number" domain={levelAxisDomain(tableData)} allowDecimals={false} className="text-xs" />
                    <YAxis className="text-xs" tickFormatter={fmtInt} />
                    <Tooltip formatter={(v) => fmtInt(v)} labelFormatter={(l) => `Уровень ${l}`} />
                    <Legend />
                    {VOLUME.series.map((sr) => (
                      <Bar key={sr.key} dataKey={sr.key} name={sr.label} fill={sr.color} isAnimationActive={false} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={volumeData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="level" type="number" domain={levelAxisDomain(tableData)} allowDecimals={false} className="text-xs" />
              <YAxis className="text-xs" tickFormatter={fmtInt} />
              <Tooltip formatter={(v) => fmtInt(v)} labelFormatter={(l) => `Уровень ${l}`} />
              <Legend />
              {VOLUME.series.map((sr) => (
                <Bar key={sr.key} dataKey={sr.key} name={sr.label} fill={sr.color} isAnimationActive={false} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </section>
    </div>
  );
}

export default LevelFunnelCharts;
